import React, { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Bell, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

/**
 * StateAlertCTA — sticky email-capture card at the bottom of the Sample
 * Report page. Collects emails into Mongo (`state_alert_subscribers`) for
 * a passive "state threshold change" alert list. See
 * backend/routes/public_routes.py::state_alert_subscribe.
 *
 * Intentionally isolated:
 *   • no navigation changes
 *   • no changes to the existing "Audit My Real Store" CTA above it
 *   • no cross-page state or context wiring
 *   • dedupe on email (server-side upsert) — safe to submit twice
 */
const EMAIL_RE = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
const ACK_KEY = "regulens_state_alert_subscribed";

export default function StateAlertCTA() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(() => {
    try { return !!localStorage.getItem(ACK_KEY); } catch { return false; }
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!EMAIL_RE.test(trimmed)) {
      toast.error("Please enter a valid email address.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/public/state-alert-subscribe", { email: trimmed });
      try { localStorage.setItem(ACK_KEY, trimmed); } catch { /* ok */ }
      setDone(true);
      toast.success("You're on the list — we'll alert you when a state moves.");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      className="mt-8 rounded-lg border border-[var(--rg-sage)] bg-[var(--rg-surface)] p-7 sm:p-9"
      data-testid="sample-state-alert-cta"
    >
      <div className="max-w-3xl mx-auto text-center">
        <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.24em] font-bold text-[var(--rg-sage)] mb-3">
          <Bell className="w-3.5 h-3.5" />
          Threshold alerts
        </div>
        <h3
          className="font-heading font-extrabold text-2xl sm:text-3xl text-[var(--rg-text-strong)] mb-2 leading-tight"
          data-testid="sample-state-alert-headline"
        >
          State compliance laws change constantly.
        </h3>
        <p
          className="text-sm text-[var(--rg-text)] mb-6 leading-relaxed"
          data-testid="sample-state-alert-subheadline"
        >
          Drop your email and we&apos;ll send a brief alert the moment a state changes its Shopify sales tax or privacy thresholds.
        </p>

        {done ? (
          <div
            className="inline-flex items-center gap-2 rounded-md border border-[var(--rg-sage)] bg-[var(--rg-sage-soft)] px-4 py-2.5 text-sm font-medium text-[var(--rg-text-strong)]"
            data-testid="sample-state-alert-done"
          >
            <CheckCircle2 className="w-4 h-4 text-[var(--rg-sage)]" />
            You&apos;re subscribed — we&apos;ll be in touch when the next state moves.
          </div>
        ) : (
          <form
            onSubmit={handleSubmit}
            className="flex flex-col sm:flex-row items-stretch gap-2 max-w-lg mx-auto"
            data-testid="sample-state-alert-form"
          >
            <Input
              type="email"
              inputMode="email"
              autoComplete="email"
              placeholder="you@yourstore.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={busy}
              className="bg-[var(--rg-bg)] border-[var(--rg-border)] text-[var(--rg-text-strong)] h-11"
              data-testid="sample-state-alert-email"
              aria-label="Email address"
              required
            />
            <Button
              type="submit"
              disabled={busy}
              className="bg-[var(--rg-gold)] text-[var(--rg-on-gold)] hover:bg-[var(--rg-gold-hover)] font-semibold h-11 px-6"
              data-testid="sample-state-alert-submit"
            >
              {busy ? "Sending..." : "Get Alerts"}
            </Button>
          </form>
        )}
        <p className="text-[11px] text-[var(--rg-muted)] mt-4 leading-snug">
          Aggregate email only — no marketing sequences, no third-party sharing. Unsubscribe by replying to any alert.
        </p>
      </div>
    </section>
  );
}
